import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { buildCustomerEmailDraft, gmailComposeUrl } from "@/lib/outreach";
import { Mail, MailX, ExternalLink, Paperclip } from "lucide-react";
import Modal from "@/components/Modal";
import { toast } from "sonner";

/**
 * BulkEmailModal — compose a separate Gmail draft for each of several selected
 * customers at once. One tab per customer lets you review/edit every draft; a
 * single "Open in Gmail" opens one prefilled Gmail compose tab per recipient.
 *
 * Like SendMessageModal's email path, this is entirely client-side: we just
 * open Gmail compose windows for the logged-in user — no provider credentials
 * and no server-side send.
 */
export default function BulkEmailModal({ customerIds, onClose, onSent }) {
  const { user } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [drafts, setDrafts] = useState({}); // { [id]: { to, subject, body } }
  const [active, setActive] = useState(0);
  const [loading, setLoading] = useState(true);

  const idsKey = useMemo(() => customerIds.join(","), [customerIds]);

  // Selection can span pages, so fetch the full record for every selected id
  // rather than relying on the page's already-loaded customers.
  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const ids = idsKey ? idsKey.split(",") : [];
        const results = await Promise.all(
          ids.map((id) => api.get(`/customers/${id}`).then((r) => r.data).catch(() => null))
        );
        if (!alive) return;
        const list = results.filter(Boolean);
        setCustomers(list);
        const d = {};
        for (const c of list) {
          const draft = buildCustomerEmailDraft(c, user);
          d[c.id] = { to: draft.to || "", subject: draft.subject || "", body: draft.body || "" };
        }
        setDrafts(d);
      } catch (e) {
        toast.error("Failed to prepare emails");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  const updateDraft = (id, patch) => setDrafts((d) => ({ ...d, [id]: { ...d[id], ...patch } }));

  const hasEmail = (c) => (drafts[c.id]?.to || "").trim().length > 0;
  const readyCustomers = customers.filter(hasEmail);

  const openCompose = (c) => {
    const dft = drafts[c.id];
    window.open(gmailComposeUrl({ to: dft.to.trim(), subject: dft.subject, body: dft.body }), "_blank", "noopener,noreferrer");
  };

  const openOne = (c) => {
    if (!hasEmail(c)) return toast.error("Add an email address first");
    openCompose(c);
    toast.success(`Opening Gmail for ${c.company}…`);
  };

  // Open one Gmail compose tab per recipient. Fired straight from the click so
  // the browser keeps them all inside the same user gesture (best chance of not
  // being popup-blocked); if any are blocked the user can allow popups & retry.
  const openAll = () => {
    if (readyCustomers.length === 0) return toast.error("None of the selected customers have an email address");
    readyCustomers.forEach(openCompose);
    toast.success(`Opening ${readyCustomers.length} Gmail draft${readyCustomers.length > 1 ? "s" : ""}…`, {
      description: "If some tabs didn't open, allow pop-ups for this site and try again.",
      duration: 6000,
    });
    onSent && onSent();
    onClose();
  };

  const activeCustomer = customers[active];
  const activeDraft = activeCustomer ? drafts[activeCustomer.id] : null;

  const emailOptions = activeCustomer?.emails || [];
  const selectedEmails = (activeDraft?.to || "").split(",").map((s) => s.trim()).filter(Boolean);
  const toggleEmail = (v) => {
    const next = selectedEmails.includes(v)
      ? selectedEmails.filter((e) => e !== v)
      : [...selectedEmails, v];
    updateDraft(activeCustomer.id, { to: next.join(", ") });
  };

  const noEmailCount = customers.length - readyCustomers.length;

  return (
    <Modal title="Send Email" subtitle={`To ${customers.length || customerIds.length} customer${customerIds.length > 1 ? "s" : ""}`} onClose={onClose} size="lg">
      <div data-testid="bulk-email-modal">
        {loading ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-2)" }}>Preparing emails…</div>
        ) : customers.length === 0 ? (
          <div style={{ padding: 32, textAlign: "center", color: "var(--text-2)" }}>Couldn't load the selected customers.</div>
        ) : (
          <>
            <div style={{ fontSize: 12, color: "var(--text-2)", marginBottom: 14 }}>
              Review each draft below, then open them all in Gmail to send from your account —
              one compose tab opens per customer.
            </div>

            <div className="card-ledger flex items-start gap-3" style={{ padding: 12, marginBottom: 14, background: "#FFF7EC", borderColor: "#F4D2BB" }} data-testid="product-images-attach-banner">
              <Paperclip size={16} style={{ color: "var(--primary)", flexShrink: 0, marginTop: 2 }} />
              <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                <strong style={{ color: "var(--text)" }}>Attach product images</strong> — this draft mentions attached photos, but
                Gmail's compose window can't add files automatically, so drag them into each draft before sending.
              </div>
            </div>

            {/* Sender — always the logged-in user, same as single-customer email */}
            {user && (
              <div style={{ marginBottom: 14 }}>
                <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>From (sender)</label>
                <div className="input-base" style={{ display: "flex", alignItems: "center", gap: 8, background: "var(--surface-2)" }} data-testid="bulk-email-from">
                  <span style={{ width: 8, height: 8, borderRadius: 2, background: user.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 13 }}>
                    <strong>{user.name}</strong>
                    <span className="font-mono-data" style={{ color: "var(--text-2)", marginLeft: 8 }}>{user.email}</span>
                  </span>
                </div>
              </div>
            )}

            {/* Customer tabs */}
            <div
              className="flex gap-1"
              style={{ overflowX: "auto", borderBottom: "1px solid var(--border)", marginBottom: 16, paddingBottom: 2 }}
              data-testid="bulk-email-tabs"
            >
              {customers.map((c, i) => {
                const isActive = i === active;
                const missing = !hasEmail(c);
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setActive(i)}
                    data-testid={`bulk-email-tab-${c.id}`}
                    className="flex items-center gap-1.5"
                    style={{
                      flexShrink: 0,
                      padding: "8px 12px",
                      fontSize: 13,
                      fontWeight: isActive ? 700 : 500,
                      color: isActive ? "var(--primary)" : missing ? "var(--text-2)" : "var(--text)",
                      borderBottom: `2px solid ${isActive ? "var(--primary)" : "transparent"}`,
                      marginBottom: -3,
                      whiteSpace: "nowrap",
                      background: "none",
                      cursor: "pointer",
                    }}
                    title={missing ? `${c.company} — no email on file` : c.company}
                  >
                    {missing && <MailX size={12} style={{ color: "var(--danger)" }} />}
                    <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis" }}>{c.company}</span>
                  </button>
                );
              })}
            </div>

            {/* Active customer draft */}
            {activeCustomer && activeDraft && (
              <div className="grid gap-4" data-testid={`bulk-email-draft-${activeCustomer.id}`}>
                <div>
                  <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>To (email)</label>
                  <input
                    value={activeDraft.to}
                    onChange={(e) => updateDraft(activeCustomer.id, { to: e.target.value })}
                    className="input-base"
                    placeholder="name@company.com, other@company.com"
                    data-testid="bulk-email-to"
                  />
                  {emailOptions.length > 1 && (
                    <div className="flex flex-wrap gap-1 mt-2 items-center">
                      <span style={{ fontSize: 10, color: "var(--text-2)", marginRight: 2 }}>Tap to add / remove recipients:</span>
                      {emailOptions.map((v) => {
                        const on = selectedEmails.includes(v);
                        return (
                          <button
                            key={v}
                            type="button"
                            onClick={() => toggleEmail(v)}
                            className="tag"
                            style={{
                              fontSize: 10,
                              cursor: "pointer",
                              background: on ? "var(--primary)" : "var(--surface-2)",
                              borderColor: on ? "var(--primary)" : "var(--border)",
                              color: on ? "#fff" : "var(--text-2)",
                            }}
                          >
                            {v}
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {!hasEmail(activeCustomer) && (
                    <div style={{ fontSize: 11, color: "var(--danger)", marginTop: 6 }}>
                      No email on file for {activeCustomer.company} — add one above to include it.
                    </div>
                  )}
                </div>

                <div>
                  <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Subject</label>
                  <input
                    value={activeDraft.subject}
                    onChange={(e) => updateDraft(activeCustomer.id, { subject: e.target.value })}
                    className="input-base"
                    data-testid="bulk-email-subject"
                  />
                </div>

                <div>
                  <label className="overline" style={{ fontSize: 10, display: "block", marginBottom: 6 }}>Message</label>
                  <textarea
                    value={activeDraft.body}
                    onChange={(e) => updateDraft(activeCustomer.id, { body: e.target.value })}
                    rows={11}
                    className="input-base"
                    style={{ resize: "vertical", fontFamily: "inherit", whiteSpace: "pre-wrap" }}
                    data-testid="bulk-email-body"
                  />
                </div>

                <div>
                  <button onClick={() => openOne(activeCustomer)} className="btn-outline" disabled={!hasEmail(activeCustomer)} data-testid="bulk-email-open-one" style={{ fontSize: 13 }}>
                    <Mail size={14} /> Open just this one in Gmail <ExternalLink size={13} />
                  </button>
                </div>
              </div>
            )}

            <div className="flex items-center gap-3 justify-end mt-6" style={{ borderTop: "1px solid var(--border)", paddingTop: 16 }}>
              {noEmailCount > 0 && (
                <span style={{ fontSize: 12, color: "var(--text-2)", marginRight: "auto" }}>
                  {noEmailCount} without an email will be skipped
                </span>
              )}
              <button onClick={onClose} className="btn-outline" data-testid="bulk-email-cancel">Cancel</button>
              <button
                onClick={openAll}
                disabled={readyCustomers.length === 0}
                className="btn-primary"
                data-testid="bulk-email-open-all"
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <Mail size={14} />
                Open in Gmail ({readyCustomers.length})
                <ExternalLink size={13} />
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
